import { Layers } from 'lucide-react'
import { useCallback, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { useListProjects } from '@/api/assessments.hook'
import { useGetCompany } from '@/api/company.hook'

import { useTrackCallback } from '@/lib/analytics/events'
import { useHasPermissions } from '@/lib/app/check-permissions.hook'
import { useDrawerNavigationStore } from '@/lib/app/drawer-navigation.hook'
import { UrlParam, useUrlParams } from '@/lib/url/url-param.hook'
import { buildSettingsSectionUrl } from '@/lib/url/urls'

import { useAssessmentAnalytics } from '@/pages/assessment/analytics'
import { BulkUpdateStatusModal } from '@/pages/assessment/assessment-tabs/bulk/bulk-update-status-modal'
import { CONTROLS_TAB_LABELS } from '@/pages/assessment/assessment-tabs/labels'
import { ReanalyzeAssessmentControlsButton } from '@/pages/assessment/assessment-tabs/reanalyze-assessment-controls-button'
import { getAssessmentControlColumnDefs } from '@/pages/assessment/config/assessment-control-column-defs'
import { AgControlTable, type AgControlTableRef } from '@/pages/control/ag-grid-control'
import type { ViewName } from '@/pages/control/ag-grid-control/config/views'
import type { ControlRow } from '@/pages/control/ag-grid-control/types'
import { getResolvedControlRowClassName } from '@/pages/control/ag-grid-control/utils/get-resolved-control-row-class-name'
import { getPreferredControlDrawerTab } from '@/pages/control/utils'

import { BulkActionsBar } from '@/components/bulk-actions-bar/bulk-actions-bar'
import { Empty } from '@/components/lemmala-design-system/empty/empty'
import { AgDataTableTrackingContext } from '@/components/lemmala-design-system/table'

const controlsFilterTracking: AgDataTableTrackingContext = {
  eventName: 'third-party.assessment.controls.filter.apply',
}

type ControlsTabProps = {
  assessmentId: string
  projectId?: string
  companyId: string
  controls: ControlRow[]
  isLoading: boolean
  readOnly?: boolean
}

export const ControlsTab = ({
  assessmentId,
  controls,
  projectId,
  companyId,
  isLoading,
  readOnly = false,
}: ControlsTabProps) => {
  const trackingContext = useAssessmentAnalytics()
  const hasManagePermissions = useHasPermissions(['base.manage'])
  const { replaceParams } = useUrlParams()

  const controlTableRef = useRef<AgControlTableRef>(null)
  const [selectedControlIds, setSelectedControlIds] = useState<string[]>([])
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false)

  const { data: companyData } = useGetCompany(companyId)
  const trackingProps = useMemo(
    () => ({
      company: companyData?.company?.profile?.name,
      company_id: companyId,
      assessment_id: assessmentId,
    }),
    [companyData, companyId, assessmentId],
  )

  const trackControlSelected = useTrackCallback('assessment.controls.control-selected')
  const trackAllSelected = useTrackCallback('assessment.controls.all-selected')
  const trackAllDeselected = useTrackCallback('assessment.controls.all-deselected')
  const trackSelectionCleared = useTrackCallback('assessment.controls.selection-cleared')
  const trackBulkUpdateClicked = useTrackCallback('assessment.controls.bulk-update-clicked')
  const trackScopeFilter = useTrackCallback('third-party.assessment.controls.scope.click')

  const handleSelectionEvent = useCallback(
    (type: 'row-selected' | 'all-selected' | 'all-deselected', count: number) => {
      if (type === 'row-selected') {
        trackControlSelected({ ...trackingProps, tab: 'controls', selected_count: count })
      } else if (type === 'all-selected') {
        trackAllSelected({ ...trackingProps, tab: 'controls', selected_count: count })
      } else {
        trackAllDeselected({ ...trackingProps, tab: 'controls' })
      }
    },
    [trackControlSelected, trackAllSelected, trackAllDeselected, trackingProps],
  )

  const clearSelection = useCallback(() => {
    trackSelectionCleared({ ...trackingProps, selected_count: selectedControlIds.length })
    controlTableRef.current?.clearSelection()
    setSelectedControlIds([])
  }, [trackSelectionCleared, trackingProps, selectedControlIds.length])

  const handleUpdateStatus = useCallback(() => {
    trackBulkUpdateClicked({
      ...trackingProps,
      tab: 'controls',
      selected_count: selectedControlIds.length,
    })
    setIsUpdateModalOpen(true)
  }, [trackBulkUpdateClicked, trackingProps, selectedControlIds.length])

  const handleViewChange = useCallback(
    (viewName: ViewName) => {
      if (!trackingContext) {
        return
      }

      trackScopeFilter({
        ...trackingContext,
        scope: viewName,
      })
    },
    [trackScopeFilter, trackingContext],
  )

  const handleGroupHeaderClick = useCallback(
    (scopeName: string) => {
      if (!trackingContext) {
        return
      }

      trackScopeFilter({
        ...trackingContext,
        scope: scopeName,
      })
    },
    [trackScopeFilter, trackingContext],
  )

  // Track groupByScope state from AgControlTable
  const [groupByScope, setGroupByScope] = useState(false)
  const trackGroupByToggle = useTrackCallback('assessment.controls.group-by.toggle')
  const handleGroupByChange = useCallback(
    (enabled: boolean) => {
      setGroupByScope(enabled)
      trackGroupByToggle({ enabled })
    },
    [trackGroupByToggle],
  )

  const columnDefs = useMemo(
    () =>
      getAssessmentControlColumnDefs({
        controls,
        readOnly,
        hasManagePermissions,
        groupByScope,
      }),
    [controls, readOnly, hasManagePermissions, groupByScope],
  )

  const handleVisibleRowOrderChange = useDrawerNavigationStore()

  const { data: projects } = useListProjects()
  const hasProjects = !!(projects?.projects?.length && projects?.projects?.length > 0)

  if (controls?.length === 0) {
    return (
      <Empty
        icon='secondary'
        title={CONTROLS_TAB_LABELS.NO_CONTROLS_TITLE}
        subtitle={hasProjects ? CONTROLS_TAB_LABELS.NO_CONTROLS_SUBTITLE : undefined}
        button={
          hasProjects
            ? {
                asChild: true,
                label: (
                  <Link
                    to={buildSettingsSectionUrl('projects', { [UrlParam.PROJECT_ID]: projectId })}
                  >
                    {CONTROLS_TAB_LABELS.GO_TO_PROJECT_SETTINGS}
                  </Link>
                ),
                size: 'sm',
              }
            : undefined
        }
        className='rounded-md border border-main-border-default bg-main-background-card py-12'
      />
    )
  }

  return (
    <>
      <div className='pb-6'>
        <AgControlTable
          ref={controlTableRef}
          controls={controls}
          isLoading={isLoading}
          filterContext={`control_assessment_${projectId ? `${companyId}_${projectId}` : companyId}`}
          readOnly={readOnly}
          tableHeaderActions={
            readOnly ? undefined : <ReanalyzeAssessmentControlsButton assessmentId={assessmentId} />
          }
          disableViewSelection
          hideEvidenceRequest
          columnDefs={columnDefs}
          getRowClassName={getResolvedControlRowClassName}
          rowHeight='double'
          dynamicHeight={false}
          groupByDefault={false}
          trackingContext={controlsFilterTracking}
          selection={{
            enabled: true,
            multiple: true,
            onSelectionChanged: setSelectedControlIds,
            onSelectionEvent: handleSelectionEvent,
          }}
          onRowClick={(control) => {
            replaceParams({
              update: [
                [UrlParam.CONTROL_ID, control.id],
                [UrlParam.CONTROL_TIMELINE_TAB, getPreferredControlDrawerTab(control)],
              ],
            })
          }}
          onVisibleRowOrderChange={handleVisibleRowOrderChange}
          onGroupByChange={handleGroupByChange}
          onViewChange={handleViewChange}
          onGroupHeaderClick={handleGroupHeaderClick}
        />
      </div>

      {selectedControlIds.length > 0 && (
        <BulkActionsBar
          selectedCount={selectedControlIds.length}
          entityLabel='control'
          actions={[
            {
              id: 'update-status',
              label: 'Update status',
              icon: <Layers className='size-4' />,
              onClick: handleUpdateStatus,
            },
          ]}
          onClose={clearSelection}
        />
      )}

      {isUpdateModalOpen && (
        <BulkUpdateStatusModal
          assessmentId={assessmentId}
          selectedControlIds={selectedControlIds}
          tab='controls'
          companyId={companyId}
          onClose={() => setIsUpdateModalOpen(false)}
        />
      )}
    </>
  )
}
